import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, of, Subscription, firstValueFrom } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { FilesService } from '../../../core/services/files.service';
import { OrgMediaAsset, OrgMediaAssetUsage, OrgMediaService } from '../../../core/services/org-media.service';
import { ProjectsService } from '../../../core/services/projects.service';
import { PropertiesService } from '../../../core/services/properties.service';
import { PropertyUploadService } from '../../../core/services/property-upload.service';
import { NotificationService } from '../../../services/notification.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { isImageLikeFileName } from '../../../shared/utils/media-library-popup.util';
import { selectAuthStatus } from '../../../store/auth/auth.selectors';

type WorkspaceKind = 'developer' | 'partner' | 'sales' | 'admin';
type MediaLibraryTab = 'view' | 'upload';
type MediaSlotRule = {
    visibility: 'public' | 'private';
    type: 'image' | 'file';
};
type MediaLibraryRouteContext = {
    assetId: string | null;
    targetType: 'org' | 'project' | 'property' | 'phase';
    targetId: string;
    slotKey: string;
    targetLabel: string;
    visibility: '' | 'public' | 'private';
};

@Component({
    selector: 'app-org-media-library',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, LucideIconsModule],
    templateUrl: './org-media-library.component.html',
    styleUrls: ['./org-media-library.component.scss']
})
export class OrgMediaLibraryComponent implements OnInit, OnDestroy {
    private media = inject(OrgMediaService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private uploadService = inject(PropertyUploadService);
    private projectsService = inject(ProjectsService);
    private propertiesService = inject(PropertiesService);
    private filesService = inject(FilesService);
    private notifications = inject(NotificationService);
    private store = inject(Store);

    private authSub: Subscription | null = null;
    private routeSub: Subscription | null = null;
    private routeContext: MediaLibraryRouteContext | null = null;
    private routeContextApplied = false;
    private uploadPreviewUrl: string | null = null;
    private routeWorkspace: WorkspaceKind | null = null;
    private requestedOrgId = '';

    @ViewChild('uploadInput')
    private uploadInput?: ElementRef<HTMLInputElement>;

    workspace: WorkspaceKind = 'developer';
    orgId = '';
    activeTab: MediaLibraryTab = 'view';
    openedAsPopup = false;

    loading = true;
    uploading = false;
    saving = false;
    assigning = false;
    deleting = false;
    error: string | null = null;

    visibilityFilter: '' | 'public' | 'private' = '';
    searchTerm = '';
    assets: OrgMediaAsset[] = [];
    usages: OrgMediaAssetUsage[] = [];
    projects: Array<{ id: string; name: string }> = [];
    properties: Array<{ id: string; label: string }> = [];
    phaseTargets: Array<{ id: string; label: string }> = [];

    selectedAssetId: string | null = null;
    selectedUploadFile: File | null = null;
    selectedUploadPreviewUrl: string | null = null;

    uploadForm = {
        display_name: '',
        description: '',
        is_public: true
    };

    editForm = {
        display_name: '',
        description: '',
        is_public: true
    };

    assignmentForm = {
        target_type: 'project' as 'org' | 'project' | 'property' | 'phase',
        target_id: '',
        target_ids: [] as string[],
        slot_key: 'cover',
        is_main: false,
        sort_order: null as number | null,
        alt: ''
    };

    readonly popupSlotRules: Record<'org' | 'project' | 'property' | 'phase', Record<string, MediaSlotRule>> = {
        org: {
            logo: { visibility: 'public', type: 'image' },
            offer_cover: { visibility: 'public', type: 'image' }
        },
        project: {
            cover: { visibility: 'public', type: 'image' },
            brochure: { visibility: 'public', type: 'file' },
            master_plan: { visibility: 'public', type: 'file' },
            location_map: { visibility: 'public', type: 'image' },
            offer_logo: { visibility: 'public', type: 'image' },
            second_phase_plan: { visibility: 'public', type: 'image' },
            detailed_phase: { visibility: 'public', type: 'image' },
            unit_plan: { visibility: 'public', type: 'image' },
            offer_slide_about: { visibility: 'public', type: 'image' },
            offer_slide_tagline: { visibility: 'public', type: 'image' },
            offer_slide_phases: { visibility: 'public', type: 'image' },
            offer_slide_contact: { visibility: 'public', type: 'image' },
            offer_slide_closing: { visibility: 'public', type: 'image' }
        },
        property: {
            floor_plan: { visibility: 'public', type: 'image' },
            layout_image: { visibility: 'public', type: 'image' },
            offer_highlight: { visibility: 'public', type: 'image' },
            gallery: { visibility: 'public', type: 'image' }
        },
        phase: {
            offer_image: { visibility: 'public', type: 'image' }
        }
    };

    readonly slotOptions: Record<'org' | 'project' | 'property' | 'phase', Array<{ value: string; label: string }>> = {
        org: [
            { value: 'logo', label: 'Organization logo' },
            { value: 'offer_cover', label: 'Organization offer cover' }
        ],
        project: [
            { value: 'cover', label: 'Project cover / offer cover' },
            { value: 'brochure', label: 'Project brochure' },
            { value: 'master_plan', label: 'Project master plan' },
            { value: 'location_map', label: 'Location map' },
            { value: 'offer_logo', label: 'Offer logo' },
            { value: 'second_phase_plan', label: 'Second phase plan' },
            { value: 'detailed_phase', label: 'Detailed phase' },
            { value: 'unit_plan', label: 'Unit plan' },
            { value: 'offer_slide_about', label: 'Offer about slide' },
            { value: 'offer_slide_tagline', label: 'Offer tagline slide' },
            { value: 'offer_slide_phases', label: 'Offer phases slide' },
            { value: 'offer_slide_contact', label: 'Offer contact slide' },
            { value: 'offer_slide_closing', label: 'Offer closing slide' }
        ],
        property: [
            { value: 'floor_plan', label: 'Unit floor plan' },
            { value: 'layout_image', label: 'Unit layout image' },
            { value: 'offer_highlight', label: 'Offer highlight image' },
            { value: 'gallery', label: 'Gallery image' }
        ],
        phase: [
            { value: 'offer_image', label: 'Phase offer image' }
        ]
    };

    ngOnInit(): void {
        const routeWorkspace = String(this.route.snapshot.data?.['workspace'] || '').trim().toLowerCase();
        if (routeWorkspace === 'developer' || routeWorkspace === 'partner' || routeWorkspace === 'sales' || routeWorkspace === 'admin') {
            this.routeWorkspace = routeWorkspace;
            this.workspace = routeWorkspace;
        }
        this.authSub = this.store.select(selectAuthStatus).subscribe(({ actor }) => {
            const roleCode = String(actor?.role_code || '').trim().toLowerCase();
            const actorType = String(actor?.type || '').trim().toLowerCase();
            const actorScope = String(actor?.actor_type || actor?.type || '').trim().toLowerCase();
            const isPartnerLikeActor =
                actorScope === 'org'
                || actorScope === 'subpartner'
                || actorScope === 'sales'
                || actorScope === 'admin'
                || actorType === 'org'
                || actorType === 'subpartner'
                || actorType === 'sales'
                || actorType === 'admin'
                || roleCode === 'app_admin';
            if (actor && !isPartnerLikeActor) {
                void this.router.navigate(['/properties']);
                return;
            }
            const actorOrgId = String(actor?.org_id || '').trim();
            this.orgId = this.routeWorkspace === 'admin' && this.requestedOrgId ? this.requestedOrgId : actorOrgId;
            if (this.routeWorkspace) {
                this.workspace = this.routeWorkspace;
            } else if (roleCode === 'subpartner' || actorType === 'subpartner' || actorType === 'partner' || actorScope === 'subpartner' || actorScope === 'partner') {
                this.workspace = 'partner';
            } else if (roleCode === 'sales' || actorType === 'sales' || actorScope === 'sales') {
                this.workspace = 'sales';
            } else if (roleCode === 'app_admin' || actorType === 'admin' || actorScope === 'admin') {
                this.workspace = 'admin';
            } else {
                this.workspace = 'developer';
            }
            if (this.assignmentForm.target_type === 'org' && this.orgId) {
                this.assignmentForm.target_id = this.orgId;
            }
        });
        this.routeSub = combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, query]) => {
            const { tab, redirected } = this.captureRouteContext(params, query);
            if (redirected) {
                void this.router.navigate(['../', tab], {
                    relativeTo: this.route,
                    queryParamsHandling: 'preserve',
                    replaceUrl: true
                });
                return;
            }
            void this.reload();
        });
    }

    ngOnDestroy(): void {
        this.authSub?.unsubscribe();
        this.routeSub?.unsubscribe();
        this.revokeUploadPreviewUrl();
    }

    get selectedAsset(): OrgMediaAsset | null {
        return this.assets.find((asset) => asset.id === this.selectedAssetId) || null;
    }

    get filteredAssets(): OrgMediaAsset[] {
        const query = String(this.searchTerm || '').trim().toLowerCase();
        return this.assets.filter((asset) => {
            if (!this.assetMatchesActivePopupContext(asset)) return false;
            return [
                asset.display_name,
                asset.file_name,
                asset.description,
                asset.content_type,
                asset.is_public ? 'public' : 'private'
            ].some((value) => String(value || '').toLowerCase().includes(query));
        });
    }

    get isDirectAttachPopupMode(): boolean {
        return Boolean(
            this.openedAsPopup
            && this.routeContext?.slotKey
            && this.routeContext?.targetType
            && (this.routeContext?.targetType === 'org' || this.routeContext?.targetId)
        );
    }

    get directAttachTargetLabel(): string {
        const context = this.routeContext;
        if (!context) return '';
        if (context.targetType === 'org') return 'Current organization';
        return String(context.targetLabel || '').trim() || String(context.targetId || '').trim();
    }

    get directAttachSlotLabel(): string {
        const context = this.routeContext;
        if (!context) return 'Selected field';
        return this.slotOptions[context.targetType]?.find((slot) => slot.value === context.slotKey)?.label || 'Selected field';
    }

    get targetOptions(): Array<{ id: string; label: string }> {
        if (this.assignmentForm.target_type === 'org') {
            return this.orgId ? [{ id: this.orgId, label: 'Current organization' }] : [];
        }
        if (this.assignmentForm.target_type === 'project') {
            return this.projects.map((project) => ({ id: project.id, label: project.name }));
        }
        if (this.assignmentForm.target_type === 'phase') {
            if (this.phaseTargets.length) return this.phaseTargets;
            const targetId = String(this.routeContext?.targetId || '').trim();
            if (!targetId) return [];
            return [{
                id: targetId,
                label: String(this.routeContext?.targetLabel || '').trim() || 'Current phase'
            }];
        }
        return this.properties.map((property) => ({ id: property.id, label: property.label }));
    }

    get activeSlotOptions(): Array<{ value: string; label: string }> {
        return this.slotOptions[this.assignmentForm.target_type] || [];
    }

    get isPropertyTargetMultiSelect(): boolean {
        return this.assignmentForm.target_type === 'property';
    }

    workspaceTitle(): string {
        if (this.workspace === 'admin') return 'Admin media library';
        if (this.workspace === 'partner') return 'Partner media library';
        if (this.workspace === 'sales') return 'Sales media library';
        return 'Organization media library';
    }

    mediaTabLink(tab: MediaLibraryTab): string[] {
        return ['/', this.workspace, 'media', tab];
    }

    async reload(): Promise<void> {
        this.loading = true;
        this.error = null;
        const scopedOrgId = this.orgId || null;

        forkJoin({
            assets: this.media.listAssets(this.visibilityFilter, scopedOrgId),
            projects: this.projectsService.getProjectsAuth({ page: 1, perPage: 250 }).pipe(
                map((result) => (result.items || []).filter((project: any) => !scopedOrgId || String(project?.organizationId || '').trim() === scopedOrgId)),
                catchError(() => of([]))
            ),
            properties: this.propertiesService.getPropertiesAuth({}, { page: 1, perPage: 250 }).pipe(
                map((result) => (result.items || []).filter((property: any) => !scopedOrgId || String(property?.organizationId || '').trim() === scopedOrgId)),
                catchError(() => of([]))
            )
        })
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: ({ assets, projects, properties }) => {
                    this.assets = Array.isArray(assets) ? assets : [];
                    this.projects = (Array.isArray(projects) ? projects : []).map((project: any) => ({
                        id: String(project?.id || ''),
                        name: String(project?.name || project?.title || 'Untitled')
                    })).filter((row) => row.id);
                    this.properties = (Array.isArray(properties) ? properties : []).map((property: any) => ({
                        id: String(property?.id || ''),
                        label: String(property?.unitNumber || property?.unit_number || property?.title || 'Property')
                    })).filter((row) => row.id);

                    const nextSelectedId = this.selectedAssetId && this.assets.some((asset) => asset.id === this.selectedAssetId)
                        ? this.selectedAssetId
                        : (this.routeContext?.assetId && this.assets.some((asset) => asset.id === this.routeContext?.assetId)
                            ? this.routeContext.assetId
                            : this.assets[0]?.id || null);
                    this.selectAsset(nextSelectedId, { silentUsageReload: false });
                    this.applyRouteContext();
                },
                error: (err: any) => {
                    this.error = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load media library';
                }
            });
    }

    onUploadFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.setSelectedUploadFile(input.files?.[0] || null);
    }

    clearUploadSelection(): void {
        this.setSelectedUploadFile(null);
        if (this.uploadInput?.nativeElement) {
            this.uploadInput.nativeElement.value = '';
        }
    }

    async createAsset(): Promise<void> {
        if (this.uploading) return;
        if (!this.selectedUploadFile) {
            this.notifications.showError('Choose an image or PDF first.').subscribe();
            return;
        }

        this.uploading = true;
        this.error = null;
        try {
            const uploaded = await firstValueFrom(
                this.uploadService.uploadFile(this.selectedUploadFile, {
                    owner: 'org',
                    isPublic: this.uploadForm.is_public,
                    orgId: this.orgId || null
                })
            );
            const created = await firstValueFrom(
                this.media.createAsset({
                    file_id: String(uploaded?.file_id || ''),
                    display_name: this.uploadForm.display_name || null,
                    description: this.uploadForm.description || null,
                    org_id: this.orgId || null
                })
            );
            this.notifications.showSuccess('Media asset added to the library.').subscribe();
            this.uploadForm = { display_name: '', description: '', is_public: this.uploadForm.is_public };
            this.clearUploadSelection();
            const createdAssetId = String(created?.id || '').trim();
            if (createdAssetId) {
                await this.router.navigate(this.mediaTabLink('view'), {
                    queryParams: { asset_id: createdAssetId },
                    queryParamsHandling: 'merge'
                });
            } else {
                await this.reload();
            }
        } catch (err: any) {
            this.error = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to create media asset';
            this.notifications.showError(this.error).subscribe();
        } finally {
            this.uploading = false;
        }
    }

    selectAsset(assetId: string | null, options: { silentUsageReload?: boolean } = {}): void {
        this.selectedAssetId = assetId;
        const asset = this.selectedAsset;
        this.usages = [];
        if (!asset) return;

        this.editForm = {
            display_name: String(asset.display_name || ''),
            description: String(asset.description || ''),
            is_public: Boolean(asset.is_public)
        };

        if (!this.activeSlotOptions.some((slot) => slot.value === this.assignmentForm.slot_key)) {
            this.assignmentForm.slot_key = this.activeSlotOptions[0]?.value || '';
        }
        if (this.assignmentForm.target_type === 'org' && this.orgId) {
            this.assignmentForm.target_id = this.orgId;
            this.assignmentForm.target_ids = [this.orgId];
        } else if (this.assignmentForm.target_type === 'property') {
            const validIds = new Set(this.targetOptions.map((option) => option.id));
            const selectedIds = this.assignmentForm.target_ids.filter((id) => validIds.has(id));
            this.assignmentForm.target_ids = selectedIds.length ? selectedIds : (this.targetOptions[0]?.id ? [this.targetOptions[0].id] : []);
            this.assignmentForm.target_id = this.assignmentForm.target_ids[0] || '';
        } else if (!this.targetOptions.some((option) => option.id === this.assignmentForm.target_id)) {
            this.assignmentForm.target_id = this.targetOptions[0]?.id || '';
            this.assignmentForm.target_ids = this.assignmentForm.target_id ? [this.assignmentForm.target_id] : [];
        }

        if (!options.silentUsageReload) {
            this.loadUsages(asset.id);
        }
    }

    onTargetTypeChange(): void {
        const slots = this.activeSlotOptions;
        this.assignmentForm.slot_key = slots[0]?.value || '';
        if (this.assignmentForm.target_type === 'org' && this.orgId) {
            this.assignmentForm.target_id = this.orgId;
            this.assignmentForm.target_ids = [this.orgId];
            return;
        }
        this.assignmentForm.target_id = this.targetOptions[0]?.id || '';
        this.assignmentForm.target_ids = this.assignmentForm.target_type === 'property'
            ? (this.assignmentForm.target_id ? [this.assignmentForm.target_id] : [])
            : (this.assignmentForm.target_id ? [this.assignmentForm.target_id] : []);
    }

    loadUsages(assetId: string): void {
        this.media.getUsages(assetId, this.orgId || null).subscribe({
            next: (usages) => {
                this.usages = Array.isArray(usages) ? usages : [];
            },
            error: () => {
                this.usages = [];
            }
        });
    }

    async saveMetadata(): Promise<void> {
        if (this.saving || !this.selectedAsset) return;
        this.saving = true;
        try {
            const updated = await firstValueFrom(
                this.media.updateAsset(this.selectedAsset.id, {
                    display_name: this.editForm.display_name || null,
                    description: this.editForm.description || null,
                    is_public: this.editForm.is_public,
                    org_id: this.orgId || null
                })
            );
            this.notifications.showSuccess('Media asset updated.').subscribe();
            await this.reload();
            this.selectAsset(updated?.id || this.selectedAsset.id, { silentUsageReload: false });
        } catch (err: any) {
            const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to update asset';
            this.notifications.showError(message).subscribe();
        } finally {
            this.saving = false;
        }
    }

    async assignSelectedAsset(): Promise<void> {
        if (this.assigning || !this.selectedAsset) return;
        const targetIds = this.resolveAssignmentTargetIds();
        if (!targetIds.length || !this.assignmentForm.slot_key) {
            this.notifications.showError('Choose a valid assignment target first.').subscribe();
            return;
        }

        this.assigning = true;
        try {
            let assignedCount = 0;
            for (const targetId of targetIds) {
                await firstValueFrom(
                    this.media.assignAsset(this.selectedAsset.id, {
                        target_type: this.assignmentForm.target_type,
                        target_id: targetId,
                        slot_key: this.assignmentForm.slot_key,
                        is_main: this.assignmentForm.slot_key === 'gallery' ? this.assignmentForm.is_main : undefined,
                        sort_order: this.assignmentForm.slot_key === 'gallery' ? this.assignmentForm.sort_order : undefined,
                        alt: this.assignmentForm.slot_key === 'gallery' ? this.assignmentForm.alt || null : undefined,
                        org_id: this.orgId || null
                    })
                );
                assignedCount += 1;
            }
            this.notifications.showSuccess(
                assignedCount > 1 ? `Asset assigned to ${assignedCount} units.` : 'Asset assigned successfully.'
            ).subscribe();
            await this.reload();
            this.selectAsset(this.selectedAsset.id, { silentUsageReload: false });
            this.notifyPopupParent('assigned', this.selectedAsset.id);
            this.closePopupSoon();
        } catch (err: any) {
            const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to assign asset';
            this.notifications.showError(message).subscribe();
        } finally {
            this.assigning = false;
        }
    }

    async unassign(usage: OrgMediaAssetUsage): Promise<void> {
        if (!this.selectedAsset) return;
        try {
            const response = await firstValueFrom(
                this.media.unassignAsset(this.selectedAsset.id, {
                    target_type: usage.target_type,
                    target_id: usage.target_id,
                    slot_key: usage.slot_key,
                    org_id: this.orgId || null
                })
            );
            this.notifications.showSuccess('Usage removed.').subscribe();
            await this.reload();
            this.selectAsset(response.asset?.id || this.selectedAsset.id, { silentUsageReload: true });
            this.usages = response.usages || [];
        } catch (err: any) {
            const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to unassign asset';
            this.notifications.showError(message).subscribe();
        }
    }

    async deleteSelectedAsset(): Promise<void> {
        if (this.deleting || !this.selectedAsset) return;
        this.deleting = true;
        try {
            await firstValueFrom(this.media.deleteAsset(this.selectedAsset.id, this.orgId || null));
            this.notifications.showSuccess('Media asset removed from the library.').subscribe();
            const deletedId = this.selectedAsset.id;
            await this.reload();
            if (this.selectedAssetId === deletedId) {
                this.selectAsset(this.assets[0]?.id || null, { silentUsageReload: false });
            }
        } catch (err: any) {
            const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to delete asset';
            this.notifications.showError(message).subscribe();
        } finally {
            this.deleting = false;
        }
    }

    openAsset(asset: OrgMediaAsset | null): void {
        if (!asset?.file_id) return;
        if (asset.preview_url) {
            window.open(asset.preview_url, '_blank', 'noopener');
            return;
        }
        this.filesService.downloadPrivateFile(asset.file_id).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener');
                window.setTimeout(() => URL.revokeObjectURL(url), 60000);
            },
            error: () => {
                this.notifications.showError('Unable to open the selected asset.').subscribe();
            }
        });
    }

    assetTypeLabel(asset: OrgMediaAsset | null): string {
        const contentType = String(asset?.content_type || '').toLowerCase();
        if (contentType.startsWith('image/')) return 'Image';
        if (contentType === 'application/pdf') return 'PDF';
        return contentType || 'File';
    }

    assetPreviewUrl(asset: OrgMediaAsset | null): string | null {
        const url = String(asset?.preview_url || '').trim();
        return url || null;
    }

    assetBadgeLabel(asset: OrgMediaAsset | null): string {
        return asset?.is_public ? 'Public' : 'Private';
    }

    uploadFileTypeLabel(): string {
        const file = this.selectedUploadFile;
        if (!file) return 'Image or PDF';
        const type = String(file.type || '').toLowerCase();
        if (type.startsWith('image/')) return 'Image';
        if (type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'PDF';
        return type || 'File';
    }

    uploadFileIcon(): string {
        const file = this.selectedUploadFile;
        if (!file) return 'upload-cloud';
        if (file.type.toLowerCase().startsWith('image/') || isImageLikeFileName(file.name)) return 'image';
        if (file.type.toLowerCase() === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'file-text';
        return 'file';
    }

    uploadFileSizeLabel(): string {
        const size = Number(this.selectedUploadFile?.size || 0);
        if (!Number.isFinite(size) || size <= 0) return '0 B';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    private captureRouteContext(params: ParamMap, query: ParamMap): { tab: MediaLibraryTab; redirected: boolean } {
        const popup = ['1', 'true', 'yes'].includes(String(query.get('popup') || '').trim().toLowerCase());
        const rawTab = String(params.get('tab') || '').trim().toLowerCase();
        const tab: MediaLibraryTab = rawTab === 'upload' ? 'upload' : 'view';
        const visibilityRaw = String(query.get('visibility') || '').trim().toLowerCase();
        const targetTypeRaw = String(query.get('target_type') || '').trim().toLowerCase();
        const targetType = targetTypeRaw === 'org' || targetTypeRaw === 'project' || targetTypeRaw === 'property' || targetTypeRaw === 'phase'
            ? targetTypeRaw
            : 'project';

        const visibility = visibilityRaw === 'public' || visibilityRaw === 'private'
            ? visibilityRaw
            : '';
        this.requestedOrgId = String(query.get('org_id') || '').trim();
        if (this.routeWorkspace === 'admin' && this.requestedOrgId) {
            this.orgId = this.requestedOrgId;
        }

        this.activeTab = tab;
        this.visibilityFilter = visibility;
        this.openedAsPopup = popup;
        this.routeContextApplied = false;
        this.routeContext = {
            assetId: String(query.get('asset_id') || '').trim() || null,
            targetType,
            targetId: String(query.get('target_id') || '').trim(),
            slotKey: String(query.get('slot_key') || '').trim().toLowerCase(),
            targetLabel: String(query.get('target_label') || '').trim(),
            visibility
        };
        return {
            tab,
            redirected: rawTab !== 'view' && rawTab !== 'upload'
        };
    }

    private applyRouteContext(): void {
        if (this.routeContextApplied || !this.routeContext) return;

        const context = this.routeContext;
        this.assignmentForm.target_type = context.targetType;
        this.onTargetTypeChange();

        if (context.slotKey && this.activeSlotOptions.some((slot) => slot.value === context.slotKey)) {
            this.assignmentForm.slot_key = context.slotKey;
        }

        const targetId = context.targetType === 'org' ? this.orgId : context.targetId;
        if (targetId && (this.targetOptions.some((option) => option.id === targetId) || context.targetType === 'phase')) {
            this.assignmentForm.target_id = targetId;
            this.assignmentForm.target_ids = targetId ? [targetId] : [];
        }

        if (this.isDirectAttachPopupMode && !this.assets.some((asset) => asset.id === this.selectedAssetId && this.assetMatchesActivePopupContext(asset))) {
            this.selectedAssetId = this.filteredAssets[0]?.id || null;
        }

        this.routeContextApplied = true;
    }

    private assetMatchesActivePopupContext(asset: OrgMediaAsset | null): boolean {
        if (!asset) return false;
        if (!this.isDirectAttachPopupMode || !this.routeContext) return true;
        const rule = this.popupSlotRules[this.routeContext.targetType]?.[this.routeContext.slotKey];
        if (!rule) return true;
        if (rule.visibility === 'public' && !asset.is_public) return false;
        const contentType = String(asset.content_type || '').trim().toLowerCase();
        const fileName = String(asset.file_name || '').trim().toLowerCase();
        if (rule.type === 'image') {
            return contentType.startsWith('image/') || isImageLikeFileName(fileName);
        }
        return contentType.startsWith('image/') || contentType === 'application/pdf' || fileName.endsWith('.pdf');
    }

    private resolveAssignmentTargetIds(): string[] {
        if (this.assignmentForm.target_type === 'org') {
            return this.orgId ? [this.orgId] : [];
        }
        if (this.assignmentForm.target_type === 'property') {
            return Array.from(
                new Set(
                    (Array.isArray(this.assignmentForm.target_ids) ? this.assignmentForm.target_ids : [])
                        .map((value) => String(value || '').trim())
                        .filter(Boolean)
                )
            );
        }
        const targetId = String(this.assignmentForm.target_id || '').trim();
        return targetId ? [targetId] : [];
    }

    private setSelectedUploadFile(file: File | null): void {
        this.selectedUploadFile = file;
        this.revokeUploadPreviewUrl();
        if (file && (String(file.type || '').toLowerCase().startsWith('image/') || isImageLikeFileName(file.name))) {
            this.uploadPreviewUrl = URL.createObjectURL(file);
            this.selectedUploadPreviewUrl = this.uploadPreviewUrl;
        } else {
            this.selectedUploadPreviewUrl = null;
        }
    }

    private revokeUploadPreviewUrl(): void {
        if (this.uploadPreviewUrl && this.uploadPreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.uploadPreviewUrl);
        }
        this.uploadPreviewUrl = null;
        this.selectedUploadPreviewUrl = null;
    }

    private notifyPopupParent(action: 'assigned', assetId: string): void {
        if (!this.openedAsPopup || typeof window === 'undefined') return;
        const parentWindow = this.resolvePopupParentWindow();
        if (!parentWindow) return;
        try {
            parentWindow.postMessage(
                {
                    type: 'nibni:media-library',
                    action,
                    workspace: this.workspace,
                    asset_id: assetId,
                    target_type: this.assignmentForm.target_type,
                    target_id: this.assignmentForm.target_type === 'org' ? this.orgId : this.resolveAssignmentTargetIds()[0] || this.assignmentForm.target_id,
                    slot_key: this.assignmentForm.slot_key
                },
                window.location.origin
            );
        } catch {
            // best-effort
        }
    }

    private closePopupSoon(): void {
        if (!this.openedAsPopup || typeof window === 'undefined') return;
        if (window.parent && window.parent !== window) return;
        window.setTimeout(() => {
            try {
                window.close();
            } catch {
                // ignore
            }
        }, 180);
    }

    private resolvePopupParentWindow(): Window | null {
        if (typeof window === 'undefined') return null;
        if (window.opener) return window.opener;
        if (window.parent && window.parent !== window) return window.parent;
        return null;
    }
}
